import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Loader2, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import RichTextEditor from "./RichTextEditor";
import useDecks from "../hooks/useDecks";
import DeckPicker from "./decks/DeckPicker";
import { apiErrorMessage } from "../services/apiError";

const EditMemoryModal = ({ show, onHide, memory, onSave }) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [selectedDecks, setSelectedDecks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { decks: availableDecks, loading: loadingDecks } = useDecks();

  useEffect(() => {
    if (memory) {
      setQuestion(memory.question || "");
      setAnswer(memory.answer || "");
      setSelectedDecks(memory.decks || []);
      setError(null);
    }
  }, [memory]);

  const handleDeckToggle = (deckId) => {
    setSelectedDecks((prev) =>
      prev.includes(deckId)
        ? prev.filter((id) => id !== deckId)
        : [...prev, deckId],
    );
  };

  const handleSave = async () => {
    if (!question.trim() || !answer.trim()) {
      setError("Both question and answer are required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(memory.id, {
        question: question.trim(),
        answer: answer.trim(),
        decks: selectedDecks,
      });
      onHide();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setQuestion("");
      setAnswer("");
      setSelectedDecks([]);
      setError(null);
      onHide();
    }
  };

  return (
    // Static backdrop: clicking outside never closes;
    // Escape and the X do, unless a save is in flight (the #28/#33 contract).
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        showCloseButton={!saving}
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => saving && event.preventDefault()}
        // The Question editor takes focus (Lexical's AutoFocusPlugin), not
        // the first tabbable, which was Cancel (#34 review A).
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">
            Edit Memory
          </DialogTitle>
          <DialogDescription className="sr-only">
            Edit this flashcard&apos;s question, answer, and decks
          </DialogDescription>
        </DialogHeader>
        <div
          data-slot="dialog-body"
          className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
        >
          {error && (
            <Alert variant="destructive" className="relative mb-4 pr-10">
              <AlertDescription>{error}</AlertDescription>
              <DismissButton onClick={() => setError(null)} />
            </Alert>
          )}

          <div className="mb-4 grid gap-1.5">
            <Label id="edit-memory-question-label">Question</Label>
            <RichTextEditor
              initialContent={memory?.question || ""}
              onChange={setQuestion}
              placeholder="Enter your question..."
              autoFocus
              ariaLabelledBy="edit-memory-question-label"
            />
          </div>

          <div className="mb-4 grid gap-1.5">
            <Label id="edit-memory-answer-label">Answer</Label>
            <RichTextEditor
              initialContent={memory?.answer || ""}
              onChange={setAnswer}
              placeholder="Enter your answer..."
              ariaLabelledBy="edit-memory-answer-label"
            />
          </div>

          <div className="grid gap-1.5">
            <Label id="edit-memory-decks-label">Decks</Label>
            <DeckPicker
              decks={availableDecks}
              loading={loadingDecks}
              selected={selectedDecks}
              onToggle={handleDeckToggle}
              labelledBy="edit-memory-decks-label"
            />
            <p className="text-sm text-muted-foreground">
              Tap a deck to add this card to it, tap again to take it out.
            </p>
          </div>
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !question.trim() || !answer.trim()}
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

EditMemoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  memory: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

/** The X on a dismissible alert. */
function DismissButton({ onClick }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="absolute top-1.5 right-1.5 size-7"
      aria-label="Close"
      onClick={onClick}
    >
      <X />
    </Button>
  );
}

DismissButton.propTypes = { onClick: PropTypes.func.isRequired };

export default EditMemoryModal;
